import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteSweep as DeleteSweepIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useGlobal } from '../contexts/GlobalContext';
import { useBuild } from '../contexts/BuildContext';

const DRAWER_WIDTH = 420;

const CODE_FONT_FAMILY = '"JetBrains Mono", "Monaco", "Menlo", "Ubuntu Mono", "Courier New", monospace';

const ThinkingWave = () => (
  <Typography
    component="span"
    sx={{
      display: 'inline-block',
      '@keyframes waveThinking': {
        '0%, 100%': { opacity: 0.4 },
        '50%': { opacity: 1 },
      },
      animation: 'waveThinking 1.2s ease-in-out infinite',
    }}
  >
    Thinking
  </Typography>
);

function CodeBlock({ className, children, ...props }) {
  const match = /language-(\w+)/.exec(className || '');
  const code = String(children).replace(/\n$/, '');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box sx={{ position: 'relative', my: 1.5 }}>
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          zIndex: 1,
          color: 'rgba(255,255,255,0.7)',
          '&:hover': { color: 'white' },
        }}
        aria-label={copied ? 'Copied' : 'Copy code'}
      >
        {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <CopyIcon sx={{ fontSize: 16 }} />}
      </IconButton>
      <SyntaxHighlighter
        language={match?.[1] ?? 'text'}
        style={oneDark}
        PreTag="div"
        customStyle={{
          margin: 0,
          borderRadius: 4,
          fontFamily: CODE_FONT_FAMILY,
        }}
        showLineNumbers={false}
      >
        {code}
      </SyntaxHighlighter>
    </Box>
  );
}

const HelpPanel = ({ open, onClose, activeProduct, activeStep, steps }) => {
  const { apiKey, currentContext } = useGlobal();
  const { feedBuilderApi, selectedIndex, searchLastCall, storiesLastCall, featuresLastCall, scoringLastCall, rankingLastCall } = useBuild();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || isLoading) return;

    if (!apiKey) {
      setError('API key is required. Configure it in Settings.');
      return;
    }

    setError(null);
    setInput('');
    const userMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const conversation = [...messages, userMessage].map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const currentTab = steps[activeStep]?.label ?? null;
      const context = { 
        product: activeProduct, 
        currentTab, 
        currentContext: currentContext || {}, 
      };
      // Include build-specific data only when on build product
      if (activeProduct === 'build') {
        context.selectedIndex = selectedIndex;
        context.searchLastCall = searchLastCall;
        context.storiesLastCall = storiesLastCall;
        context.featuresLastCall = featuresLastCall;
        context.scoringLastCall = scoringLastCall;
        context.rankingLastCall = rankingLastCall;
      }
      console.log('User context', context);
      const result = await feedBuilderApi.callHelp(conversation, context);
      const reply = result?.result?.reply ?? result?.error ?? 'No response received.';
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Error: ${err?.message ?? 'Request failed'}` },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setMessages([]);
    setInput('');
    setError(null);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          maxWidth: '100vw',
          boxSizing: 'border-box',
        },
      }}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" component="h2">
            Ask Me Anything
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              onClick={handleClear}
              aria-label="clear conversation"
              disabled={messages.length === 0}
              size="small"
            >
              <DeleteSweepIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {messages.map((msg, i) => (
            <Paper
              key={i}
              elevation={0}
              sx={{
                p: 1.5,
                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '90%',
                bgcolor: msg.role === 'user' ? 'primary.main' : 'action.hover',
                color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
              }}
            >
              {msg.role === 'user' ? (
                <Typography
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: '0.9rem',
                    m: 0,
                  }}
                >
                  {msg.content}
                </Typography>
              ) : (
                <Box
                  sx={{
                    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    fontSize: '0.9rem',
                    m: 0,
                    '& p': { margin: '0 0 0.5em 0' },
                    '& p:last-child': { marginBottom: 0 },
                    '& ul, & ol': { margin: '0.25em 0', pl: 2 },
                    '& pre': { margin: 0 },
                  }}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        if (inline) {
                          return <strong style={{ fontFamily: CODE_FONT_FAMILY }}>{children}</strong>;
                        }
                        const code = String(children).replace(/\n$/, '');
                        if (code.length < 50) {
                          return <strong style={{ fontFamily: CODE_FONT_FAMILY }}>{children}</strong>;
                        }
                        return (
                          <CodeBlock className={className} {...props}>
                            {children}
                          </CodeBlock>
                        );
                      },
                    }}
                  >
                    {msg.content}
                  </ReactMarkdown>
                </Box>
              )}
            </Paper>
          ))}
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ThinkingWave />
            </Box>
          )}
          <div ref={messagesEndRef} />
          <form onSubmit={handleSubmit}>
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask a question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              size="small"
            />
          </form>
        </Box>

        {error && (
          <Typography color="error" variant="body2" sx={{ px: 2, pb: 1 }}>
            {error}
          </Typography>
        )}
      </Box>
    </Drawer>
  );
};

export default HelpPanel;
